import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useFilterJobs,
  useDeleteJob,
  useUpdateJobStatus,
  usePublishedJobsCount,
  useDraftJobsCount,
  useSearchJobs
} from '@/lib/api/hooks/job';
import type { Job } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import { ActionDropdown, TanStackTable, DeleteModal, PublishModal, UnpublishModal, type ColumnDef, type DropdownAction, Pagination, Button } from '@/components/ui';
import { Briefcase, Eye, MapPin, CalendarDays } from 'lucide-react';
import JobsStats from './JobsStats';
import { Paragraph } from '@/components/ui/Typography';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import FilterModule from '@/components/ui/FilterModule';
import Search from '@/components/ui/Search';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { useJobsFilterSections } from '@/pages/opportunities/jobs/FilterModule/FILTER_SECTIONS';

type TabType = 'PUBLISH' | 'DRAFT' | 'ENDING_SOON';



const Jobs: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('PUBLISH');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [selectedJobForDelete, setSelectedJobForDelete] = useState<Job | null>(null);
  const [selectedJobForStatusUpdate, setSelectedJobForStatusUpdate] = useState<Job | null>(null);

  const FILTER_SECTIONS = useJobsFilterSections();
                // ── URL-driven search / filter state ──────────────────────────────────────
                const {
                  mode,
                  searchTerm,
                  filterState,
                  page,
                  size,
                  setSearch,
                  setFilters,
                  removeFilter,
                  clearFilters,
                  setPage,
                  apiFilters,
                  getActiveFilterCount
                } = useSearchFilterParams(FILTER_SECTIONS)
              
              // Search API (when searching)
              const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchJobs(
              searchTerm,
                mode === 'search' && activeTab !== 'ENDING_SOON'
              )
            
              // Filter API (when filtering or default)
              const { data: filterData, isFetching: isFilterFetching, isError: isFilterError, refetch: refetchFilter } = useFilterJobs(
                { page, size },
             activeTab === 'ENDING_SOON' ? { ...apiFilters } : { status: activeTab as 'DRAFT' | 'PUBLISH', ...apiFilters }
              )
            
              // Select appropriate data source: search takes priority
              const aggregateData = mode === 'search' ? searchData : filterData
              const isFetching = mode === 'search' ? isSearchFetching : isFilterFetching
              const isError = mode === 'search' ? isSearchError : isFilterError
            
              const totalPages = aggregateData?.data?.totalPage ?? 1
              const activeFilterCount = getActiveFilterCount()
              const refetchData = mode === 'search' ? refetchSearch : refetchFilter
              const emptyMessage = mode === 'search'
                ? 'No jobs found matching your search criteria.'
                : mode === "filter" ?  'No jobs found matching your filter criteria.'
                : 'No jobs found.'


  const { data: publishedCountData } = usePublishedJobsCount();
  const { data: draftCountData } = useDraftJobsCount();

  const deleteJobMutation = useDeleteJob();
  const updateStatusMutation = useUpdateJobStatus();

  // Get jobs from search or filter
  const rawJobs = searchTerm && activeTab !== 'ENDING_SOON'
    ? (Array.isArray(searchData?.data) ? searchData.data : [])
    : (Array.isArray(filterData?.data?.data) ? filterData.data.data : []);

  // Filter search results by status to match active tab (for PUBLISH/DRAFT)
  const jobsByStatus = searchTerm && activeTab !== 'ENDING_SOON'
    ? rawJobs.filter((job: Job) => job.status === activeTab)
    : rawJobs;

  // Filter jobs based on tab and search
  const filteredJobs = useMemo(() => {
    let jobs = jobsByStatus;

    // Filter by ending soon (within 30 days) - client-side filtering needed
    if (activeTab === 'ENDING_SOON') {
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);

      jobs = jobs.filter((job: Job) => {
        const endDate = new Date(job.endDate);
        return endDate >= today && endDate <= thirtyDaysFromNow && job.status === 'PUBLISH';
      });
    }

    return jobs;
  }, [jobsByStatus, activeTab]);

  const allJobs = useMemo(() => {
    return Array.isArray(filterData?.data?.data) ? filterData.data.data : [];
  }, [filterData?.data?.data]);

  const publishedCount = publishedCountData?.data || 0;
  const draftCount = draftCountData?.data || 0;

  const endingSoonCount = useMemo(() => {
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    return allJobs.filter((job: Job) => {
      const endDate = new Date(job.endDate);
      return endDate >= today && endDate <= thirtyDaysFromNow && job.status === 'PUBLISH';
    }).length;
  }, [allJobs]);

  const handleAddJob = () => {
    navigate('/admin/jobs/add');
  };

  const handleEditJob = (job: Job) => {
    navigate(`/admin/jobs/edit/${job.jobId}`, {
      state: { job }
    });
  };

  const handleViewDetails = (job: Job) => {
    navigate(`/admin/jobs/${job.jobId}`);
  };

  const handleDeleteJob = (job: Job) => {
    setSelectedJobForDelete(job);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedJobForDelete) return;

    deleteJobMutation.mutate(selectedJobForDelete.jobId, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Job deleted successfully');
          setDeleteModalOpen(false);
          setSelectedJobForDelete(null);
          refetchFilter();
        } else {
          showToast.error('Failed to delete job');
        }
      },
      onError: (error: Error) => {
        showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
      }
    });
  };

  const handlePublishJob = (job: Job) => {
    setSelectedJobForStatusUpdate(job);
    setPublishModalOpen(true);
  };

  const handleUnpublishJob = (job: Job) => {
    setSelectedJobForStatusUpdate(job);
    setUnpublishModalOpen(true);
  };

  const confirmStatusUpdate = () => {
    if (!selectedJobForStatusUpdate) return;

    const newStatus = selectedJobForStatusUpdate.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

    updateStatusMutation.mutate(
      {
        jobId: selectedJobForStatusUpdate.jobId,
        data: { status: newStatus }
      },
      {
        onSuccess: () => {
          showToast.success(
            newStatus === 'PUBLISH'
              ? 'Job published successfully!'
              : 'Job unpublished successfully!'
          );
          setPublishModalOpen(false);
          setUnpublishModalOpen(false);
          setSelectedJobForStatusUpdate(null);
          refetchFilter();
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const getDropdownActions = (job: Job): DropdownAction[] => {
    const actions: DropdownAction[] = [
      {
        label: 'View Details',
        onClick: (e) => {
          e.stopPropagation();
          handleViewDetails(job);
        }
      },
      {
        label: 'Edit Job',
        onClick: (e) => {
          e.stopPropagation();
          handleEditJob(job);
        }
      },
      {
        label: job.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
        onClick: (e) => {
          e.stopPropagation();
          if (job.status === 'PUBLISH') {
            handleUnpublishJob(job);
          } else {
            handlePublishJob(job);
          }
        },
        disabled: updateStatusMutation.isPending
      }
    ];

    // Only show Delete for draft jobs
    if (job.status === 'DRAFT') {
      actions.push({
        label: 'Delete',
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteJob(job);
        },
        variant: 'danger' as const,
        disabled: deleteJobMutation.isPending
      });
    }

    return actions;
  };

  // ── Helpers ──
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try { return new Date(dateString).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return dateString; }
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ').filter(Boolean);
    if (!parts.length) return 'UN';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  };

  // ── Columns ──
  const columns: ColumnDef<Job, unknown>[] = [
    {
      id: 'jobTitle',
      header: 'Job Title',
      cell: ({ row }) => {
        const job = row.original;
        return (
          <div className="flex items-center gap-3 min-w-[220px]">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50">
              <Briefcase className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">{job.jobTitle}</p>
              <p className="text-xs text-gray-400 mt-0.5 font-mono">{job.jobId.slice(-13).toUpperCase()}</p>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'author',
      header: 'Author',
      cell: ({ row }) => {
        const job = row.original;
        const name = (job.publishedByName && job.publishedByName !== 'N/A') ? job.publishedByName
          : (job.createdByName && job.createdByName !== 'N/A') ? job.createdByName : 'Unknown';
        return (
          <div className="flex items-center gap-2.5 min-w-[140px]">
            <div className="h-9 w-9 shrink-0 rounded-full bg-emerald-700 text-white text-xs font-bold flex items-center justify-center">
              {getInitials(name)}
            </div>
            <span className="text-sm text-gray-900 whitespace-nowrap">{name}</span>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'posted',
      header: 'Posted',
      cell: ({ row }) => (
        <span className="text-sm text-gray-700 whitespace-nowrap">{formatDate(row.original.datePublished)}</span>
      ),
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const typeConfig: Record<string, { label: string; className: string }> = {
          FULL_TIME:  { label: 'Full-time',  className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
          PART_TIME:  { label: 'Part-time',  className: 'bg-amber-50   text-amber-700   border-amber-200'   },
          CONTRACT:   { label: 'Contract',   className: 'bg-blue-50    text-blue-700    border-blue-200'    },
          INTERNSHIP: { label: 'Internship', className: 'bg-purple-50  text-purple-700  border-purple-200'  },
        };
        const type = row.original.jobType;
        const config = typeConfig[type] ?? { label: type, className: 'bg-gray-50 text-gray-600 border-gray-200' };
        return (
          <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium ${config.className}`}>
            {config.label}
          </span>
        );
      },
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'location',
      header: 'Location',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 min-w-[120px]">
          <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="text-sm text-gray-700">{row.original.jobLocation}</span>
        </div>
      ),
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'deadline',
      header: 'Deadline',
      cell: ({ row }) => {
        const endDate = row.original.endDate;
        if (!endDate) return <span className="text-sm text-gray-400">—</span>;
        const end = new Date(endDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const daysLeft = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        const dateLabel = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        let daysText: string;
        let daysColor: string;
        if (daysLeft < 0) { daysText = 'Expired'; daysColor = 'text-red-600'; }
        else if (daysLeft === 0) { daysText = 'Today'; daysColor = 'text-red-600'; }
        else { daysText = `${daysLeft} days left`; daysColor = daysLeft <= 14 ? 'text-orange-500' : 'text-gray-500'; }
        return (
          <div className="flex items-start gap-1.5 min-w-[120px]">
            <CalendarDays className="h-4 w-4 shrink-0 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-700 leading-tight whitespace-nowrap">{dateLabel}</p>
              <p className={`text-xs font-medium ${daysColor}`}>{daysText}</p>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'views',
      header: 'Views',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5 justify-center">
          <Eye className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700">{row.original.totalViews ?? 0}</span>
        </div>
      ),
      meta: { headerClassName: 'px-4 py-4 text-center', cellClassName: 'px-4 py-4 text-center whitespace-nowrap' },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const job = row.original;
        let label: string;
        let className: string;
        if (job.status === 'DRAFT') {
          label = 'Draft';
          className = 'bg-gray-100 text-gray-600 border-gray-200';
        } else {
          const end = new Date(job.endDate);
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const daysLeft = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          if (job.isExpired || daysLeft <= 0) {
            label = 'Expired'; className = 'bg-red-50 text-red-700 border-red-200';
          } else if (daysLeft <= 30) {
            label = 'Closing Soon'; className = 'bg-orange-50 text-orange-700 border-orange-200';
          } else {
            label = 'Published'; className = 'bg-emerald-50 text-emerald-700 border-emerald-200';
          }
        }
        return (
          <span className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium ${className}`}>
            {label}
          </span>
        );
      },
      meta: { headerClassName: 'px-4 py-4', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const job = row.original;
        return (
          <div onClick={(e) => e.stopPropagation()} className="flex justify-center">
            <ActionDropdown id={job.jobId} actions={getDropdownActions(job)} />
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 text-center', cellClassName: 'px-4 py-4 text-center whitespace-nowrap' },
    },
  ];

  const isEmptyState = !isFetching && mode ==="default"  && filteredJobs.length === 0;

  return (
    <div className="space-y-4 flex flex-col gap-6">
      {/* Jobs Stats */}
      <JobsStats />
      {/* Tabs */}
      <div className="flex items-center justify-between pb-5">
        <div className="flex items-center gap-0">
          <button
            onClick={() => {
              setActiveTab('PUBLISH');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'PUBLISH'
              ? 'bg-wine-100 text-white'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Published ({publishedCount})
          </button>
          <button
            onClick={() => {
              setActiveTab('DRAFT');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'DRAFT'
              ? 'bg-wine-100 text-white'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Drafts ({draftCount})
          </button>
          <button
            onClick={() => {
              setActiveTab('ENDING_SOON');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'ENDING_SOON'
              ? 'bg-wine-100 text-white'
              : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Ending soon ({endingSoonCount})
          </button>
        </div>
        <button
          onClick={handleAddJob}
          className="bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-6 rounded-md text-sm"
        >
          Post Job
        </button>
      </div>

      {/* Jobs Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
 {/* Search + Filter Controls */}
                          <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
                            <div className="flex items-center relative gap-4 justify-between flex-wrap">
                              <Search
                                value={searchTerm}
                                onSearch={setSearch}
                                placeholder="Search Jobs"
                              />
                               <FilterModule
                                    sections={FILTER_SECTIONS}
                                    activeFilters={filterState}
                                    onApply={setFilters}
                                    activeFilterCount={activeFilterCount}
                                  />
                            </div>
                
                            {/* Active filter chips — only shown in filter mode */}
                            <FilterParamsDisplay
                              sections={FILTER_SECTIONS}
                              filterState={filterState}
                              onRemove={removeFilter}
                              onClearAll={clearFilters}
                            />
                          </div>

        {isFetching ? <TanStackTable data={[]} columns={columns} loading /> :
              isError ? (
          <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
            <Paragraph className="">Error loading data.</Paragraph>
            <Button
              onClick={() => {
                refetchData();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        ) : isEmptyState ? (
                <div className="text-center py-20">
                  <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                    <img src="/No-post-icon.png" alt="No posts" className="w-10 h-10 object-contain" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-lg">
                    {activeTab === 'PUBLISH' ? 'No Recent Jobs' : 'No Draft'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {activeTab === 'PUBLISH' ? 'You are yet to create a job' : 'Your draft is empty'}
                  </p>
                    <Button variant="primary" className="mt-4" onClick={handleAddJob}>
                      Post Job
                    </Button>
                </div>
              ) : (
          <>
          <div className="overflow-x-auto">
            <TanStackTable<Job>
              data={filteredJobs}
              columns={columns}
              getRowId={(row) => row.jobId}
              onRowClick={(row) => handleViewDetails(row)}
              tableClassName="min-w-[1200px] w-full divide-y divide-gray-200"
              theadClassName="bg-[#FAFAFA]"
              tbodyClassName="bg-white divide-y divide-gray-200"
              rowClassName="hover:bg-gray-50 cursor-pointer transition-colors"
              emptyMessage={emptyMessage}
              emptyClassName="px-6 py-12 text-center text-sm text-gray-500"
            />
          </div>
                  <Pagination
                                   currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                                className="border-t border-gray-200 p-4"
                              />
                              </>
        )}
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Job"
        subtitle="Are you sure you want to delete this job?"
        itemName={selectedJobForDelete?.jobTitle}
        warningText="This action cannot be undone. The job will be permanently deleted."
        isLoading={deleteJobMutation.isPending}
      />

      {/* Publish Modal */}
      {selectedJobForStatusUpdate && selectedJobForStatusUpdate.status === 'DRAFT' && (
        <PublishModal
          isOpen={publishModalOpen}
          onClose={() => setPublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          title="Publish Job?"
          subtitle="Are you sure you want to publish this job?"
          itemName={selectedJobForStatusUpdate.jobTitle}
          infoText="This job will be visible to the public once published."
          itemType="Job"
          isLoading={updateStatusMutation.isPending}
        />
      )}

      {/* Unpublish Modal */}
      {selectedJobForStatusUpdate && selectedJobForStatusUpdate.status === 'PUBLISH' && (
        <UnpublishModal
          isOpen={unpublishModalOpen}
          onClose={() => setUnpublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          itemType="Job"
          isLoading={updateStatusMutation.isPending}
        />
      )}
    </div>
  );
};

export default Jobs;

